import { useState, useEffect } from 'react';
import settingsService from '../../../services/settingsService';

const PaymentSettings = () => {
  const [formData, setFormData] = useState({
    currency: 'EUR',
    currencySymbol: '€',
    currencyPosition: 'after',
    defaultMatchPrice: 5,
    paymentGateways: {
      redsys: {
        enabled: true,
        testMode: true,
        merchantCode: '',
        terminal: '001',
        secretKey: '',
        environment: 'test', // 'test' o 'production'
        currency: '978', // EUR
        transactionType: '0', // Autorización
      },
      bankTransfer: {
        enabled: true,
        accountName: 'ProKidsApp',
        accountNumber: 'ES12 3456 7890 1234 5678 9012',
        bankName: 'Banco Ejemplo',
        instructions: 'Por favor, incluye el número de referencia en la transferencia.',
      },
    },
    invoices: {
      enabled: true,
      prefix: 'INV-',
      companyName: 'ProKidsApp S.L.',
      companyAddress: 'Calle Ejemplo, 123, 28001 Madrid',
      companyTaxId: 'B12345678',
      companyLogo: '/logo.png',
      footer: 'Gracias por confiar en ProKidsApp',
    },
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPaymentSettings = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await settingsService.getPaymentSettings();

        if (response.success && response.data) {
          // Combinar datos del servidor con valores por defecto
          setFormData(prevData => ({
            ...prevData,
            ...response.data
          }));
        }
      } catch (error) {
        console.error('Error al cargar configuración de pagos:', error);
        setError('Error al cargar la configuración de pagos');
      } finally {
        setLoading(false);
      }
    };

    loadPaymentSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: type === 'checkbox' ? checked : value,
          },
        });
      } else if (parts.length === 3) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: {
              ...formData[parts[0]][parts[1]],
              [parts[2]]: type === 'checkbox' ? checked : value,
            },
          },
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      console.log('Enviando datos de configuración de pagos:', formData);

      const response = await settingsService.updatePaymentSettings(formData);

      if (response.success) {
        setSuccess(true);
        console.log('Configuración de pagos guardada correctamente');
      } else {
        throw new Error(response.message || 'Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error al guardar configuración de pagos:', error);
      setError(error.message || 'Error al guardar la configuración de pagos');
    } finally {
      setLoading(false);
    }
  };

  if (loading && Object.keys(formData).length === 0) {
    return <div className="loading">Cargando configuración...</div>;
  }

  return (
    <div className="settings-section">
      <h2>Configuración de Pagos</h2>
      <p className="settings-description">Configura los ajustes de pagos y facturación.</p>

      {success && <div className="alert alert-success">Configuración guardada correctamente.</div>}

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="settings-card">
          <h3>Moneda</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currency">Moneda</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="EUR">Euro (EUR)</option>
                <option value="USD">Dólar estadounidense (USD)</option>
                <option value="GBP">Libra esterlina (GBP)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="currencySymbol">Símbolo</label>
              <input
                type="text"
                id="currencySymbol"
                name="currencySymbol"
                value={formData.currencySymbol}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="currencyPosition">Posición del símbolo</label>
              <select
                id="currencyPosition"
                name="currencyPosition"
                value={formData.currencyPosition}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="before">Antes (€5)</option>
                <option value="after">Después (5€)</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="defaultMatchPrice">Precio predeterminado de partido</label>
            <div className="input-group">
              <input
                type="number"
                id="defaultMatchPrice"
                name="defaultMatchPrice"
                value={formData.defaultMatchPrice}
                onChange={handleInputChange}
                className="form-control"
                min="0"
                step="0.01"
                required
              />
              <span className="input-group-text">{formData.currencySymbol}</span>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3>Pasarelas de Pago</h3>

          <div className="payment-gateway-section">
            <div className="payment-gateway-header">
              <div className="payment-gateway-title">
                <h4>Redsys</h4>
                <span className="payment-gateway-logo">
                  <span className="material-icons">credit_card</span>
                </span>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="paymentGateways.redsys.enabled"
                  name="paymentGateways.redsys.enabled"
                  checked={formData.paymentGateways.redsys.enabled}
                  onChange={handleInputChange}
                />
                <label htmlFor="paymentGateways.redsys.enabled">Activar Redsys</label>
              </div>
            </div>

            {formData.paymentGateways.redsys.enabled && (
              <div className="payment-gateway-content">
                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="paymentGateways.redsys.testMode"
                    name="paymentGateways.redsys.testMode"
                    checked={formData.paymentGateways.redsys.testMode}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="paymentGateways.redsys.testMode">Modo de prueba</label>
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.redsys.merchantCode">Código de comercio</label>
                  <input
                    type="text"
                    id="paymentGateways.redsys.merchantCode"
                    name="paymentGateways.redsys.merchantCode"
                    value={formData.paymentGateways.redsys.merchantCode}
                    onChange={handleInputChange}
                    className="form-control"
                    required={formData.paymentGateways.redsys.enabled}
                    placeholder="999008881"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.redsys.terminal">Terminal</label>
                  <input
                    type="text"
                    id="paymentGateways.redsys.terminal"
                    name="paymentGateways.redsys.terminal"
                    value={formData.paymentGateways.redsys.terminal}
                    onChange={handleInputChange}
                    className="form-control"
                    required={formData.paymentGateways.redsys.enabled}
                    placeholder="001"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.redsys.secretKey">Clave secreta</label>
                  <input
                    type="password"
                    id="paymentGateways.redsys.secretKey"
                    name="paymentGateways.redsys.secretKey"
                    value={formData.paymentGateways.redsys.secretKey}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="••••••••"
                    required={formData.paymentGateways.redsys.enabled}
                  />
                  <small className="form-text">Clave proporcionada por Redsys para firmar transacciones</small>
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.redsys.environment">Entorno</label>
                  <select
                    id="paymentGateways.redsys.environment"
                    name="paymentGateways.redsys.environment"
                    value={formData.paymentGateways.redsys.environment}
                    onChange={handleInputChange}
                    className="form-control"
                    required={formData.paymentGateways.redsys.enabled}
                  >
                    <option value="test">Pruebas</option>
                    <option value="production">Producción</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="payment-gateway-section">
            <div className="payment-gateway-header">
              <div className="payment-gateway-title">
                <h4>Transferencia Bancaria</h4>
                <span className="payment-gateway-logo">
                  <span className="material-icons">account_balance</span>
                </span>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="paymentGateways.bankTransfer.enabled"
                  name="paymentGateways.bankTransfer.enabled"
                  checked={formData.paymentGateways.bankTransfer.enabled}
                  onChange={handleInputChange}
                />
                <label htmlFor="paymentGateways.bankTransfer.enabled">
                  Activar Transferencia Bancaria
                </label>
              </div>
            </div>

            {formData.paymentGateways.bankTransfer.enabled && (
              <div className="payment-gateway-content">
                <div className="form-group">
                  <label htmlFor="paymentGateways.bankTransfer.accountName">
                    Nombre de la cuenta
                  </label>
                  <input
                    type="text"
                    id="paymentGateways.bankTransfer.accountName"
                    name="paymentGateways.bankTransfer.accountName"
                    value={formData.paymentGateways.bankTransfer.accountName}
                    onChange={handleInputChange}
                    className="form-control"
                    required={formData.paymentGateways.bankTransfer.enabled}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.bankTransfer.accountNumber">
                    Número de cuenta
                  </label>
                  <input
                    type="text"
                    id="paymentGateways.bankTransfer.accountNumber"
                    name="paymentGateways.bankTransfer.accountNumber"
                    value={formData.paymentGateways.bankTransfer.accountNumber}
                    onChange={handleInputChange}
                    className="form-control"
                    required={formData.paymentGateways.bankTransfer.enabled}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.bankTransfer.bankName">Nombre del banco</label>
                  <input
                    type="text"
                    id="paymentGateways.bankTransfer.bankName"
                    name="paymentGateways.bankTransfer.bankName"
                    value={formData.paymentGateways.bankTransfer.bankName}
                    onChange={handleInputChange}
                    className="form-control"
                    required={formData.paymentGateways.bankTransfer.enabled}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="paymentGateways.bankTransfer.instructions">Instrucciones</label>
                  <textarea
                    id="paymentGateways.bankTransfer.instructions"
                    name="paymentGateways.bankTransfer.instructions"
                    value={formData.paymentGateways.bankTransfer.instructions}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="settings-card">
          <h3>Facturas</h3>
          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="invoices.enabled"
              name="invoices.enabled"
              checked={formData.invoices.enabled}
              onChange={handleInputChange}
            />
            <label htmlFor="invoices.enabled">Generar facturas automáticamente</label>
          </div>

          {formData.invoices.enabled && (
            <>
              <div className="form-group">
                <label htmlFor="invoices.prefix">Prefijo de factura</label>
                <input
                  type="text"
                  id="invoices.prefix"
                  name="invoices.prefix"
                  value={formData.invoices.prefix}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="invoices.companyName">Nombre de la empresa</label>
                <input
                  type="text"
                  id="invoices.companyName"
                  name="invoices.companyName"
                  value={formData.invoices.companyName}
                  onChange={handleInputChange}
                  className="form-control"
                  required={formData.invoices.enabled}
                />
              </div>
              <div className="form-group">
                <label htmlFor="invoices.companyAddress">Dirección de la empresa</label>
                <textarea
                  id="invoices.companyAddress"
                  name="invoices.companyAddress"
                  value={formData.invoices.companyAddress}
                  onChange={handleInputChange}
                  className="form-control"
                  rows="2"
                  required={formData.invoices.enabled}
                ></textarea>
              </div>
              <div className="form-group">
                <label htmlFor="invoices.companyTaxId">CIF/NIF</label>
                <input
                  type="text"
                  id="invoices.companyTaxId"
                  name="invoices.companyTaxId"
                  value={formData.invoices.companyTaxId}
                  onChange={handleInputChange}
                  className="form-control"
                  required={formData.invoices.enabled}
                />
              </div>
              <div className="form-group">
                <label htmlFor="invoices.companyLogo">URL del logo para facturas</label>
                <input
                  type="text"
                  id="invoices.companyLogo"
                  name="invoices.companyLogo"
                  value={formData.invoices.companyLogo}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="invoices.footer">Pie de página para facturas</label>
                <textarea
                  id="invoices.footer"
                  name="invoices.footer"
                  value={formData.invoices.footer}
                  onChange={handleInputChange}
                  className="form-control"
                  rows="2"
                ></textarea>
              </div>
            </>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentSettings;
